import {Button, Card, Input, Label, makeStyles, MessageBar, Spinner, Title3,} from "@fluentui/react-components";
import {Checkmark24Regular} from "@fluentui/react-icons";
import React, {useState} from "react";
import {useApiMutation} from "../../../services/useApiQuery.ts";
import {Group} from "../../../services/types.ts";
import {queryClient} from "../../../services/queryClient.ts";
import {useAtomValue, useSetAtom} from "jotai/index";
import {appIdAtom, errorAtom, groupsTabViewAtom, teamsGroupLoadingAtom} from "../../../store.ts";
import {useTranslation} from "react-i18next";

export interface Participant {
    id: string,
    assignedTeam?: { id: string }
}


interface ConfirmGroupModalProps {
    selectedParticipants: Participant[]
    closeModal: () => void;
    postSuccess: () => void;
}

const useStyles = makeStyles({
    formField: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        marginRight: '16px'
    }
});

export const ConfirmAudienceModal: React.FC<ConfirmGroupModalProps> = (props: ConfirmGroupModalProps) => {
    const styles = useStyles();
    const setCreateGroupInProgress = useSetAtom(teamsGroupLoadingAtom);
    const [groupName, setGroupName] = useState<string>('');
    const appId = useAtomValue(appIdAtom);
    const setGroupView = useSetAtom(groupsTabViewAtom)
    const setError = useSetAtom(errorAtom)
    const {t} = useTranslation()

    const createGroupMutation = useApiMutation<Group, {
        groupName: string;
        teamsAppId: string,
        participants: { userId: string, teamId: string }[]
    }>('POST', {
        onMutate: async () => {
            setCreateGroupInProgress(true);
            setGroupView('list')
        },
        onSuccess: async () => {
            await queryClient.refetchQueries({
                queryKey: ['api', '/api/group/list'],
            })
            props.postSuccess();
            setGroupName('');
        },
        onError: (error: any) => {
            setError(error?.message || 'Failed to create audience');
        },
        onSettled: () => {
            setCreateGroupInProgress(false)
        }
    });

    const handleSubmitEnrollment = async () => {
        if (props.selectedParticipants.length === 0) {
            setError(t("error.no-audience-name"));
            return;
        }

        if (!groupName.trim()) {
            setError(t("error.no-audience-participants"));
            return;
        }

        const enrollmentData = {
            groupName: groupName.trim(),
            teamsAppId: appId?.toString()!,
            participants: props.selectedParticipants.map((participant: any) => {

                return {
                    userId: participant.id,
                    teamId: participant.assignedTeam?.id
                };
            }).filter(item => item !== null)
        };

        createGroupMutation.mutate({
            path: '/api/group/create',
            body: enrollmentData
        });
    };

    return <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
    }}>
        <Card style={{
            width: '400px',
            maxWidth: '90%',
            padding: '20px'
        }}>
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
            }}>
                <Title3>{t("text.confirm")}</Title3>
                <MessageBar intent="info">
                    {t("message.create-audience")} {props.selectedParticipants.length}
                </MessageBar>
                <div className={styles.formField}>
                    <Label htmlFor="groupName" required>{t("table.header.name")}</Label>
                    <Input
                        id="groupName"
                        type="text"
                        value={groupName}
                        autoComplete="off"
                        onChange={(e) => setGroupName(e.target.value)}
                        placeholder={t("placeholder.name-group")}
                        autoFocus
                    />
                </div>
                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '8px',
                    marginTop: '16px'
                }}>
                    <Button
                        appearance="primary"
                        disabled={createGroupMutation.isPending || !groupName.trim()}
                        onClick={() => {
                            props.closeModal();
                            handleSubmitEnrollment();
                        }}
                        icon={createGroupMutation.isPending ? <Spinner size="small"/> : <Checkmark24Regular/>}
                    >
                        {t("button.create-audience")}

                    </Button>
                    <Button
                        appearance="secondary"
                        onClick={() => {
                            props.closeModal();
                            setGroupName('');
                        }}
                    >
                        {t("button.cancel")}
                    </Button>
                </div>
            </div>
        </Card>
    </div>
}